import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import Page from 'flarum/common/components/Page';
import Button from 'flarum/common/components/Button';
import LogInModal from 'flarum/forum/components/LogInModal';

/**
 * #366 — 未登录访问 /dressUp 的产品官网风格落地页
 *
 * 设计稿：/Users/zhaozihui/.mavis/agents/mavis/workspace/designs/landing-page-E.html
 * 视觉规范：论坛 #app.App--dress-up 已有紫粉 radial 渐变 + #212121 暗底
 *          (less/forum.less L2591 #app.App--dress-up)，本组件复用同一 bodyClass
 *          → 自动继承背景渐变。
 *
 * 8 大模块（按任务清单）：
 *   1. 顶部导航 (logo + 5 锚链 + 立即开始 CTA)
 *   2. Hero (badge + 大标题 + 副标 + CTA + 前后对比 demo + 4 stats)
 *   3. 核心功能 6 卡 (Seedream / Kling / 装备库 / 历史 / 预设模特 / PWA)
 *   4. 使用步骤 4 步 (上传 → 选装备 → AI 生成 → 分享)
 *   5. 精选案例 5 网格 (2fr 1fr 1fr / 1fr 1fr)
 *   6. 价格/配额 3 卡 (免费/Pro/专业，Pro featured amber)
 *   7. FAQ 5 项 (hover 展开手风琴)
 *   8. CTA + Footer (CTA 旋转光晕 + 5 链接 + 版权)
 *
 * 交互：
 *   - 导航/CTA "立即开始" / "登录" → 弹 Flarum LogInModal (不开新页)
 *   - 登录成功后由 Flarum app.session.user 变化触发 redraw，SeedreamPage 守卫变 'ok'
 *   - FAQ 手风琴：本组件内原生展开（不用 Modal 子类，绕开 view() 陷阱）
 *   - 锚链跳转用纯 hash (#features/#how/...)，平滑滚动交给 CSS
 */

/**
 * Flarum 1.8 translator.trans() 在某些情况下会返回 vnode 数组（[string]），
 * 直接当 JSX children 渲染时可能触发 mithril onupdate 警告。
 * 这里 flatten 为纯 string，避开 mithril 内部 normalize path 上的 replace 边界。
 */
function t(key) {
  const result = app.translator.trans(key);
  return Array.isArray(result) ? result.join('') : result;
}
export default class LandingPage extends Page {
  constructor() {
    super(...arguments);
    // 共用 #app.App--dress-up 紫粉渐变背景（less/forum.less L2591 已定义）
    _defineProperty(this, "bodyClass", 'App--dress-up LandingPage--active');
  }
  oninit(vnode) {
    super.oninit(vnode);

    // FAQ 展开状态 — key = question index
    this.faqOpen = {};
  }
  view() {
    return m("div", {
      class: "LandingPage"
    }, this.renderNav(), this.renderHero(), this.renderFeatures(), this.renderHowItWorks(), this.renderShowcase(), this.renderPricing(), this.renderFaq(), this.renderCta(), this.renderFooter());
  }

  // ========== 1. 顶部导航 ==========
  renderNav() {
    return m("nav", {
      class: "LandingNav"
    }, m("div", {
      class: "LandingContainer LandingNav-inner"
    }, m("div", {
      class: "LandingLogo"
    }, m("div", {
      class: "LandingLogo-icon"
    }, "\uD83D\uDC57"), m("span", null, t('ziven-dress-up.forum.landing.nav.brand'))), m("div", {
      class: "LandingNav-links"
    }, m("a", {
      href: "#features"
    }, t('ziven-dress-up.forum.landing.nav.features')), m("a", {
      href: "#how"
    }, t('ziven-dress-up.forum.landing.nav.how')), m("a", {
      href: "#showcase"
    }, t('ziven-dress-up.forum.landing.nav.showcase')), m("a", {
      href: "#pricing"
    }, t('ziven-dress-up.forum.landing.nav.pricing')), m("a", {
      href: "#faq"
    }, t('ziven-dress-up.forum.landing.nav.faq'))), m("div", {
      class: "LandingNav-actions"
    }, m(Button, {
      class: "LandingBtn LandingBtn-ghost-sm",
      onclick: () => this.openLoginModal()
    }, m("i", {
      class: "fas fa-sign-in-alt"
    }), ' ' + t('ziven-dress-up.forum.landing.nav.login')), m(Button, {
      class: "LandingNav-cta",
      onclick: () => this.openLoginModal()
    }, t('ziven-dress-up.forum.landing.nav.cta')))));
  }

  // ========== 2. Hero ==========
  renderHero() {
    return m("section", {
      class: "LandingHero"
    }, m("div", {
      class: "LandingContainer"
    }, m("div", {
      class: "LandingHero-badge"
    }, m("span", {
      class: "LandingDot"
    }), m("span", null, t('ziven-dress-up.forum.landing.hero.badge'))), m("h1", {
      class: "LandingHero-title"
    }, t('ziven-dress-up.forum.landing.hero.title_line1'), m("br", null), t('ziven-dress-up.forum.landing.hero.title_line2_prefix'), m("span", {
      class: "LandingGradient-text"
    }, t('ziven-dress-up.forum.landing.hero.title_highlight'))), m("p", {
      class: "LandingHero-subtitle"
    }, t('ziven-dress-up.forum.landing.hero.subtitle_line1'), m("br", null), t('ziven-dress-up.forum.landing.hero.subtitle_line2')), m("div", {
      class: "LandingHero-actions"
    }, m(Button, {
      class: "LandingBtn LandingBtn-primary",
      onclick: () => this.openLoginModal()
    }, m("i", {
      class: "fas fa-magic"
    }), ' ' + t('ziven-dress-up.forum.landing.hero.cta_primary')), m("a", {
      href: "#how",
      class: "LandingBtn LandingBtn-ghost"
    }, t('ziven-dress-up.forum.landing.hero.cta_secondary'))), m("div", {
      class: "LandingHero-demo"
    }, m("div", {
      class: "LandingGlass LandingDemo-card"
    }, m("div", {
      class: "LandingDemo-label"
    }, t('ziven-dress-up.forum.landing.hero.demo_before_label')), m("div", {
      class: "LandingDemo-avatar LandingDemo-avatar--before"
    }), m("div", {
      class: "LandingDemo-name"
    }, t('ziven-dress-up.forum.landing.hero.demo_before_name'))), m("div", {
      class: "LandingGlass LandingDemo-card"
    }, m("div", {
      class: "LandingDemo-label"
    }, t('ziven-dress-up.forum.landing.hero.demo_after_label')), m("div", {
      class: "LandingDemo-avatar LandingDemo-avatar--after"
    }, "\u2728"), m("div", {
      class: "LandingDemo-name"
    }, t('ziven-dress-up.forum.landing.hero.demo_after_name')))), m("div", {
      class: "LandingHero-stats"
    }, m("div", {
      class: "LandingStat"
    }, m("div", {
      class: "LandingStat-num"
    }, t('ziven-dress-up.forum.landing.hero.stat1_num')), m("div", {
      class: "LandingStat-label"
    }, t('ziven-dress-up.forum.landing.hero.stat1_label'))), m("div", {
      class: "LandingStat"
    }, m("div", {
      class: "LandingStat-num"
    }, t('ziven-dress-up.forum.landing.hero.stat2_num')), m("div", {
      class: "LandingStat-label"
    }, t('ziven-dress-up.forum.landing.hero.stat2_label'))), m("div", {
      class: "LandingStat"
    }, m("div", {
      class: "LandingStat-num"
    }, t('ziven-dress-up.forum.landing.hero.stat3_num')), m("div", {
      class: "LandingStat-label"
    }, t('ziven-dress-up.forum.landing.hero.stat3_label'))), m("div", {
      class: "LandingStat"
    }, m("div", {
      class: "LandingStat-num"
    }, t('ziven-dress-up.forum.landing.hero.stat4_num')), m("div", {
      class: "LandingStat-label"
    }, t('ziven-dress-up.forum.landing.hero.stat4_label'))))));
  }

  // ========== 3. 核心功能 6 卡 ==========
  renderFeatures() {
    const features = [{
      icon: '🎨',
      title: 'feature_seedream_title',
      desc: 'feature_seedream_desc'
    }, {
      icon: '🎬',
      title: 'feature_kling_title',
      desc: 'feature_kling_desc'
    }, {
      icon: '📦',
      title: 'feature_equipment_title',
      desc: 'feature_equipment_desc',
      amber: true
    }, {
      icon: '📚',
      title: 'feature_history_title',
      desc: 'feature_history_desc'
    }, {
      icon: '🎭',
      title: 'feature_preset_title',
      desc: 'feature_preset_desc'
    }, {
      icon: '📱',
      title: 'feature_pwa_title',
      desc: 'feature_pwa_desc',
      amber: true
    }];
    return m("section", {
      id: "features",
      class: "LandingSection LandingSection-features"
    }, m("div", {
      class: "LandingContainer"
    }, m("div", {
      class: "LandingSection-header LandingSection-header--center"
    }, m("div", {
      class: "LandingSection-label"
    }, t('ziven-dress-up.forum.landing.features.label')), m("h2", {
      class: "LandingSection-title"
    }, t('ziven-dress-up.forum.landing.features.title_prefix'), m("span", {
      class: "LandingGradient-text"
    }, t('ziven-dress-up.forum.landing.features.title_highlight')), t('ziven-dress-up.forum.landing.features.title_suffix')), m("p", {
      class: "LandingSection-desc"
    }, t('ziven-dress-up.forum.landing.features.desc'))), m("div", {
      class: "LandingFeatures-grid"
    }, features.map((f, idx) => m("div", {
      class: 'LandingGlass LandingFeature-card' + (f.amber ? ' LandingFeature-card--amber' : ''),
      key: idx
    }, m("div", {
      class: 'LandingFeature-icon' + (f.amber ? ' LandingFeature-icon--amber' : '')
    }, f.icon), m("div", {
      class: "LandingFeature-title"
    }, t('ziven-dress-up.forum.landing.features.' + f.title)), m("div", {
      class: "LandingFeature-desc"
    }, t('ziven-dress-up.forum.landing.features.' + f.desc)))))));
  }

  // ========== 4. 使用步骤 4 步 ==========
  renderHowItWorks() {
    const steps = [{
      num: 1,
      emoji: '📷',
      title: 'step1_title',
      desc: 'step1_desc'
    }, {
      num: 2,
      emoji: '👗',
      title: 'step2_title',
      desc: 'step2_desc'
    }, {
      num: 3,
      emoji: '🤖',
      title: 'step3_title',
      desc: 'step3_desc'
    }, {
      num: 4,
      emoji: '✨',
      title: 'step4_title',
      desc: 'step4_desc'
    }];
    return m("section", {
      id: "how",
      class: "LandingSection LandingSection-how"
    }, m("div", {
      class: "LandingContainer"
    }, m("div", {
      class: "LandingSection-header LandingSection-header--center"
    }, m("div", {
      class: "LandingSection-label"
    }, t('ziven-dress-up.forum.landing.how.label')), m("h2", {
      class: "LandingSection-title"
    }, t('ziven-dress-up.forum.landing.how.title_prefix'), m("span", {
      class: "LandingGradient-text"
    }, t('ziven-dress-up.forum.landing.how.title_highlight'))), m("p", {
      class: "LandingSection-desc"
    }, t('ziven-dress-up.forum.landing.how.desc'))), m("div", {
      class: "LandingSteps-grid"
    }, steps.map(s => m("div", {
      class: "LandingGlass LandingStep",
      key: s.num
    }, m("div", {
      class: "LandingStep-num"
    }, s.num), m("div", {
      class: "LandingStep-emoji"
    }, s.emoji), m("div", {
      class: "LandingStep-title"
    }, t('ziven-dress-up.forum.landing.how.' + s.title)), m("div", {
      class: "LandingStep-desc"
    }, t('ziven-dress-up.forum.landing.how.' + s.desc + '_line1'), m("br", null), t('ziven-dress-up.forum.landing.how.' + s.desc + '_line2')))))));
  }

  // ========== 5. 精选案例 5 网格 ==========
  renderShowcase() {
    const items = [{
      bg: 's1',
      emoji: '👗',
      tag: 'showcase_tag_1'
    }, {
      bg: 's2',
      emoji: '🧥',
      tag: 'showcase_tag_2'
    }, {
      bg: 's3',
      emoji: '👘',
      tag: 'showcase_tag_3'
    }, {
      bg: 's4',
      emoji: '🎩',
      tag: 'showcase_tag_4'
    }, {
      bg: 's5',
      emoji: '💃',
      tag: 'showcase_tag_5'
    }];
    return m("section", {
      id: "showcase",
      class: "LandingSection LandingSection-showcase"
    }, m("div", {
      class: "LandingContainer"
    }, m("div", {
      class: "LandingSection-header LandingSection-header--center"
    }, m("div", {
      class: "LandingSection-label"
    }, t('ziven-dress-up.forum.landing.showcase.label')), m("h2", {
      class: "LandingSection-title"
    }, t('ziven-dress-up.forum.landing.showcase.title_prefix'), m("span", {
      class: "LandingGradient-text"
    }, t('ziven-dress-up.forum.landing.showcase.title_highlight')), t('ziven-dress-up.forum.landing.showcase.title_suffix'))), m("div", {
      class: "LandingShowcase-grid"
    }, items.map((item, idx) => m("div", {
      class: "LandingGlass LandingShowcase-item",
      key: idx
    }, m("div", {
      class: 'LandingShowcase-bg LandingShowcase-bg--' + item.bg
    }, item.emoji), m("div", {
      class: "LandingShowcase-tag"
    }, t('ziven-dress-up.forum.landing.showcase.' + item.tag)))))));
  }

  // ========== 6. 价格/配额 3 卡 ==========
  renderPricing() {
    const plans = [{
      name: 'free_name',
      tagline: 'free_tagline',
      price: '0',
      period: 'free_period',
      features: ['free_f1', 'free_f2', 'free_f3', 'free_f4', {
        no: true,
        key: 'free_f5'
      }, {
        no: true,
        key: 'free_f6'
      }],
      cta: 'free_cta',
      ghost: true
    }, {
      name: 'pro_name',
      tagline: 'pro_tagline',
      price: '29',
      period: 'pro_period',
      features: ['pro_f1', 'pro_f2', 'pro_f3', 'pro_f4', 'pro_f5', 'pro_f6'],
      cta: 'pro_cta',
      ghost: false,
      featured: true,
      badge: () => t('ziven-dress-up.forum.landing.pricing.pro_badge')
    }, {
      name: 'business_name',
      tagline: 'business_tagline',
      price: '99',
      period: 'business_period',
      features: ['business_f1', 'business_f2', 'business_f3', 'business_f4', 'business_f5', 'business_f6'],
      cta: 'business_cta',
      ghost: true
    }];
    return m("section", {
      id: "pricing",
      class: "LandingSection LandingSection-pricing"
    }, m("div", {
      class: "LandingContainer"
    }, m("div", {
      class: "LandingSection-header LandingSection-header--center"
    }, m("div", {
      class: "LandingSection-label"
    }, t('ziven-dress-up.forum.landing.pricing.label')), m("h2", {
      class: "LandingSection-title"
    }, t('ziven-dress-up.forum.landing.pricing.title_prefix'), m("span", {
      class: "LandingGradient-text"
    }, t('ziven-dress-up.forum.landing.pricing.title_highlight'))), m("p", {
      class: "LandingSection-desc"
    }, t('ziven-dress-up.forum.landing.pricing.desc'))), m("div", {
      class: "LandingPricing-grid"
    }, plans.map((plan, idx) => m("div", {
      class: (plan.featured ? 'LandingGlass-strong' : 'LandingGlass') + ' LandingPrice-card' + (plan.featured ? ' LandingPrice-card--featured' : ''),
      key: idx
    }, plan.featured && m("div", {
      class: "LandingPrice-badge"
    }, typeof plan.badge === 'function' ? plan.badge() : plan.badge), m("div", {
      class: "LandingPrice-name"
    }, t('ziven-dress-up.forum.landing.pricing.' + plan.name)), m("div", {
      class: "LandingPrice-tagline"
    }, t('ziven-dress-up.forum.landing.pricing.' + plan.tagline)), m("div", {
      class: "LandingPrice-amount"
    }, m("span", {
      class: "LandingPrice-currency"
    }, "\xA5"), m("span", {
      class: "LandingPrice-value"
    }, plan.price), m("span", {
      class: "LandingPrice-period"
    }, t('ziven-dress-up.forum.landing.pricing.' + plan.period))), m("ul", {
      class: "LandingPrice-features"
    }, plan.features.map((f, i) => {
      const isNo = typeof f === 'object' && f.no;
      const key = typeof f === 'object' ? f.key : f;
      return m("li", {
        class: isNo ? 'LandingPrice-feature--no' : '',
        key: i
      }, t('ziven-dress-up.forum.landing.pricing.' + key));
    })), m(Button, {
      class: 'LandingBtn LandingPrice-cta ' + (plan.ghost ? 'LandingBtn-ghost' : 'LandingBtn-primary'),
      onclick: () => this.openLoginModal()
    }, t('ziven-dress-up.forum.landing.pricing.' + plan.cta)))))));
  }

  // ========== 7. FAQ 5 项 ==========
  renderFaq() {
    const faqs = ['faq1', 'faq2', 'faq3', 'faq4', 'faq5'];
    return m("section", {
      id: "faq",
      class: "LandingSection LandingSection-faq"
    }, m("div", {
      class: "LandingContainer"
    }, m("div", {
      class: "LandingSection-header LandingSection-header--center"
    }, m("div", {
      class: "LandingSection-label"
    }, t('ziven-dress-up.forum.landing.faq.label')), m("h2", {
      class: "LandingSection-title"
    }, t('ziven-dress-up.forum.landing.faq.title_prefix'), m("span", {
      class: "LandingGradient-text"
    }, t('ziven-dress-up.forum.landing.faq.title_highlight')), t('ziven-dress-up.forum.landing.faq.title_suffix'))), m("div", {
      class: "LandingFaq-list"
    }, faqs.map((key, idx) => m("div", {
      class: 'LandingGlass LandingFaq-item' + (this.faqOpen[idx] ? ' LandingFaq-item--open' : ''),
      key: idx,
      onclick: () => this.toggleFaq(idx)
    }, m("div", {
      class: "LandingFaq-q"
    }, m("span", null, t('ziven-dress-up.forum.landing.faq.' + key + '_q')), m("span", {
      class: "LandingFaq-toggle"
    }, this.faqOpen[idx] ? '−' : '+')), m("div", {
      class: "LandingFaq-a"
    }, t('ziven-dress-up.forum.landing.faq.' + key + '_a')))))));
  }
  toggleFaq(idx) {
    // 强制重绘：m.redraw() 让 mithril 重新计算 view()
    // LandingPage 本身不是 Modal 子类, view() 是合法入口
    this.faqOpen[idx] = !this.faqOpen[idx];
    m.redraw();
  }

  // ========== 8. CTA + Footer ==========
  renderCta() {
    return m("section", {
      class: "LandingSection LandingSection-cta"
    }, m("div", {
      class: "LandingContainer"
    }, m("div", {
      class: "LandingCta-card"
    }, m("div", {
      class: "LandingCta-content"
    }, m("h2", {
      class: "LandingCta-title"
    }, t('ziven-dress-up.forum.landing.cta.title_prefix'), m("span", {
      class: "LandingGradient-text"
    }, t('ziven-dress-up.forum.landing.cta.title_highlight'))), m("p", {
      class: "LandingCta-desc"
    }, t('ziven-dress-up.forum.landing.cta.desc')), m(Button, {
      class: "LandingBtn LandingBtn-primary LandingCta-btn",
      onclick: () => this.openLoginModal()
    }, m("i", {
      class: "fas fa-magic"
    }), ' ' + t('ziven-dress-up.forum.landing.cta.button'))))));
  }
  renderFooter() {
    const links = ['about', 'terms', 'privacy', 'contact', 'forum'];
    return m("footer", {
      class: "LandingFooter"
    }, m("div", {
      class: "LandingContainer"
    }, m("div", {
      class: "LandingFooter-links"
    }, links.map((key, idx) => m("a", {
      href: "#",
      key: idx
    }, t('ziven-dress-up.forum.landing.footer.' + key)))), m("div", {
      class: "LandingFooter-copyright"
    }, t('ziven-dress-up.forum.landing.footer.copyright'))));
  }

  /**
   * 打开 Flarum 内置 LogInModal（不开新页）
   * 登录成功由 Flarum app.session.user 变化触发全局 redraw → SeedreamPage 守卫从 login_required → ok
   */
  openLoginModal() {
    app.modal.show(LogInModal);
  }
}
flarum.reg.add('ziiven-dress-up', 'forum/components/LandingPage', LandingPage);